<script setup>
import moment from "moment-timezone";
import { VDataTableServer } from "vuetify/labs/VDataTable";

moment.tz.setDefault("Asia/Jakarta");

const loading = ref();
const loadingTable = ref();
const loadingClear = ref();
const loadingCsv = ref();
const loadCsv = () => {
  loadingCsv.value = true;
  setTimeout(() => {
    loadingCsv.value = false;
  }, 3000);
};

definePage({
  meta: {
    layout: "default",
  },
});
const pageTitle = "Login User";

const breadCrumbs = [
  {
    title: "Dashboard",
    to: "/admin/dashboard",
  },
  {
    title: "System",
    to: null,
  },
  {
    title: pageTitle,
    to: null,
  },
];
const headers = [
  {
    title: "Login ID",
    key: "loginId",
  },
  {
    title: "User Name",
    key: "username",
  },
  {
    title: "Email Address",
    key: "email",
  },
  {
    title: "Roles",
    key: "roles",
  },
  {
    title: "Login Status",
    key: "loginStatus",
  },
  {
    title: "Last Login Time",
    key: "lastLoginTime",
  },
  {
    title: "Actions",
    sortable: false,
    key: "actions",
  },
];

const route = useRoute();
const router = useRouter();

const refForm = ref();

const valid = ref("");
const errors = ref("");

// Data table options
const itemsPerPage = ref(10);
const page = ref(1);
const orderBy = ref("createdAt");
const orderDirection = ref("ASC");

const searchByUsername = ref(null);
const searchByEmail = ref(null);

const { data: dataQueries, execute: fetchQueries } = await useApi(
  createUrl("/cms/users", {
    query: {
      limit: itemsPerPage,
      searchByUsername,
      searchByEmail,
      page,
      orderBy,
      orderDirection,
    },
  }),
);

const updateOptions = async (options) => {
  loadingTable.value = true;
  page.value = options.page;
  orderBy.value = options.sortBy[0]?.key;
  orderDirection.value = options.sortBy[0]?.order;
  fetchQueries().then((value) => {
    loadingTable.value = false;
  });
};

const perPages = [5, 10, 25, 50];

const refreshResult = () => {
  loadingClear.value = true;
  loadingTable.value = true;
  searchByUsername.value = null;
  searchByEmail.value = null;
  fetchQueries().then((value) => {
    loadingClear.value = false;
    loadingTable.value = false;
  });
};

const searchResult = () => {
  loading.value = true;
  loadingTable.value = true;
  refForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) {
      fetchQueries().then((value) => {
        loading.value = false;
        loadingTable.value = false;
      });
    }
  });
};

const dataItems = computed(() => (dataQueries ? dataQueries.value?.data : []));
const totalItems = computed(() =>
  dataQueries ? dataQueries.value?.metadata.pagination.itemCount : [],
);

const downloadCsv = async () => {
  try {
    loadCsv();
    const res = await $api("/cms/users/generate/csv", {
      method: "GET",
      onResponseError({ response }) {
        errors.value = response._data.message;
        setTimeout(() => (errors.value = false), 10000);
      },
    });

    valid.value = "download data success";
    const blob = new Blob([res], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "login-user.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (err) {
    errors.value = "generate csv failed";

    setTimeout(() => (errors.value = false), 10000);
  }
};

/*
 * Delete
 * */
const deleteDialogDisplayId = ref("");
const deleteDialog = ref(false);
const deleteAlert = ref(false);
const deleteItem = (i, e) => {
  deleteDialogDisplayId.value = i;
  deleteDialog.value = e;
};
const deleteOk = async (e) => {
  loadingTable.value = true;
  try {
    const res = await $api("/cms/users/" + e, {
      method: "DELETE",
      onResponseError({ response }) {
        errors.value = response._data.message;

        setTimeout(() => (errors.value = false), 10000);
      },
    });

    if (res.statusCode === 200) {
      deleteAlert.value = res.message;
    }
  } catch (err) {
    deleteAlert.value = `The record count not be deleted, because of an association`;
    errors.value = err.message;
  }
  fetchQueries().then((value) => {
    loadingTable.value = false;
    deleteAlert.value = false;
    errors.value = null;
    deleteDialog.value = false;
  });
};
const deleteCancel = () => {
  deleteDialog.value = false;
};
</script>

<template>
  <VBreadcrumbs :items="breadCrumbs" class="px-0 pt-0 pb-5" />
  <h4 class="text-h4 my-4">Data {{ pageTitle }}</h4>
  <VCard>
    <VAlert v-show="valid" type="success">
      {{ valid }}
    </VAlert>
    <VAlert v-show="errors" type="error">
      {{ errors }}
    </VAlert>
    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :headers="headers"
      :items="dataItems"
      :items-length="totalItems"
      :loading="loadingTable"
      class="text-no-wrap"
      @update:options="updateOptions"
    >
      <template #top>
        <VRow class="ms-4 me-4 my-2 justify-center justify-sm-space-between">
          <VCol cols="24" lg="8" sm="8">
            <VForm
              ref="refForm"
              @submit.prevent="
                () => {
                  searchResult();
                }
              "
            >
              <div class="d-flex flex-rows gap-4">
                <AppTextField
                  v-model="searchByUsername"
                  clearable
                  placeholder="Input User Name"
                />
                <AppTextField
                  v-model="searchByEmail"
                  :rules="[]"
                  clearable
                  placeholder="Input Email Address"
                />
                <VBtn
                  :disabled="loading"
                  :loading="loading"
                  color="primary"
                  type="submit"
                >
                  Search

                  <template #loader>
                    <span class="custom-loader">
                      <VIcon class="text-white" icon="tabler-refresh" />
                    </span>
                  </template>
                </VBtn>
                <VBtn
                  :disabled="loadingClear"
                  :loading="loadingClear"
                  color="secondary"
                  @click="refreshResult()"
                >
                  <VIcon icon="tabler-refresh" start />
                  Clear
                </VBtn>
                <VBtn
                  :disabled="loadingCsv"
                  :loading="loadingCsv"
                  color="primary"
                  type="button"
                  variant="tonal"
                  @click="downloadCsv"
                >
                  <VIcon icon="tabler-download" start />
                  Download
                  <template #loader>
                    <span class="custom-loader">
                      <VIcon class="text-white" icon="tabler-refresh" />
                    </span>
                  </template>
                </VBtn>
              </div>
            </VForm>
          </VCol>
          <VCol cols="12" lg="2" sm="2">
            <AppSelect
              v-model="itemsPerPage"
              :items="perPages"
              class="w-50 ml-auto"
              placeholder="Per Page"
            />
          </VCol>
          <VCol cols="12" lg="2" sm="2">
            <VBtn :to="{ path: 'login-user/create' }" block color="primary">
              <VIcon icon="tabler-plus" start />
              Create {{ pageTitle }}
            </VBtn>
          </VCol>
        </VRow>
      </template>
      <template #item.roles="{ item }">
        <ul v-for="role in item.roles" class="list-none">
          <li>{{ role.name }} | {{ role.type }}</li>
        </ul>
      </template>
      <template #item.lastLoginTime="{ item }"
        >{{
          item.lastLoginTime ? moment(item.lastLoginTime).format("LLL") : null
        }}
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex gap-1">
          <IconBtn :to="{ path: 'login-user/view/' + item.id }">
            <VIcon icon="tabler-edit" />
          </IconBtn>
          <IconBtn @click="deleteItem(item.loginId, item.id)">
            <VIcon icon="tabler-trash" />
          </IconBtn>
        </div>
      </template>
      <template #bottom>
        <VCardText class="pt-2">
          <div
            class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 mt-2"
          >
            <div></div>
            <VPagination
              v-model="page"
              :length="Math.ceil(totalItems / itemsPerPage)"
              :total-visible="$vuetify.display.smAndDown ? 3 : 5"
            />
          </div>
        </VCardText>
      </template>
    </VDataTableServer>
  </VCard>
  <!-- 👉 Delete Dialog  -->
  <VDialog v-model="deleteDialog" max-width="500px">
    <VCard v-show="!deleteAlert">
      <VCardTitle class="text-center my-3">
        <p>Are you sure you want to delete this item?</p>
        <h3>login ID : {{ deleteDialogDisplayId }}</h3>
      </VCardTitle>

      <VCardActions>
        <VSpacer />
        <VBtn color="secondary" variant="outlined" @click="deleteCancel">
          Cancel
        </VBtn>
        <VBtn color="error" variant="elevated" @click="deleteOk(deleteDialog)">
          OK
        </VBtn>
        <VSpacer />
      </VCardActions>
    </VCard>
    <VCard v-show="deleteAlert">
      <VCardText>
        <VAlert type="error">
          {{ deleteAlert }}
        </VAlert>
      </VCardText>
    </VCard>
  </VDialog>
</template>
